import { Controller } from "@hotwired/stimulus"

const ARROW_HIDE_DELAY_MS = 3000
const FADE_MS = 600

export default class extends Controller {
  static targets = ["slide", "segment", "arrow"]
  static values = { interval: { type: Number, default: 5000 } }

  connect() {
    this.index = 0
    this.pointerInside = false
    this.focusInside = false
    this.#show(0)
    this.#schedule()
  }

  disconnect() {
    clearTimeout(this.rotationTimer)
    clearTimeout(this.arrowTimer)
    clearTimeout(this.settleTimer)
    this.slideTargets.forEach((slide) => slide.classList.remove("is-active", "is-leaving"))
    this.segmentTargets.forEach((segment) => segment.classList.remove("is-active"))
    this.arrowTargets.forEach((arrow) => arrow.classList.remove("is-visible"))
  }

  next() {
    this.#go(this.index + 1)
    this.#showArrows()
  }

  previous() {
    this.#go(this.index - 1)
    this.#showArrows()
  }

  goTo({ params: { index } }) {
    this.#go(index)
  }

  pointerMoved() {
    this.pointerInside = true
    this.#showArrows()
    this.#schedule()
  }

  pointerLeft() {
    this.pointerInside = false
    this.#schedule()
  }

  focusEntered(event) {
    this.focusInside = event.target.matches(":focus-visible")
    this.#schedule()
  }

  focusLeft(event) {
    if (this.element.contains(event.relatedTarget)) return

    this.focusInside = false
    this.#schedule()
  }

  visibilityChanged() {
    this.#schedule()
  }

  get #paused() {
    return this.pointerInside || this.focusInside || document.hidden
  }

  #go(index) {
    const count = this.slideTargets.length
    const target = ((index % count) + count) % count

    if (target !== this.index) {
      const outgoing = this.slideTargets[this.index]
      const focusWasInOutgoing = outgoing.contains(document.activeElement)
      outgoing.classList.remove("is-active")
      outgoing.classList.add("is-leaving")
      this.#show(target)
      if (focusWasInOutgoing) this.slideTargets[target].focus()

      clearTimeout(this.settleTimer)
      this.settleTimer = setTimeout(() => this.#settle(), FADE_MS)
    }

    this.#schedule()
  }

  #show(target) {
    this.index = target
    this.slideTargets[target].classList.add("is-active")
    this.slideTargets[target].classList.remove("is-leaving")
    this.segmentTargets.forEach((segment, index) => {
      segment.classList.toggle("is-active", index === target)
      if (index === target) {
        segment.setAttribute("aria-current", "true")
      } else {
        segment.removeAttribute("aria-current")
      }
    })
  }

  #settle() {
    this.slideTargets.forEach((slide, index) => {
      if (index !== this.index) slide.classList.remove("is-leaving")
    })
  }

  #schedule() {
    clearTimeout(this.rotationTimer)
    if (this.#paused) return

    this.rotationTimer = setTimeout(() => this.#go(this.index + 1), this.intervalValue)
  }

  #showArrows() {
    this.arrowTargets.forEach((arrow) => arrow.classList.add("is-visible"))
    clearTimeout(this.arrowTimer)
    this.arrowTimer = setTimeout(() => this.#hideArrows(), ARROW_HIDE_DELAY_MS)
  }

  #hideArrows() {
    if (this.arrowTargets.some((arrow) => arrow.matches(":hover, :focus-visible"))) {
      this.arrowTimer = setTimeout(() => this.#hideArrows(), ARROW_HIDE_DELAY_MS)
      return
    }

    this.arrowTargets.forEach((arrow) => arrow.classList.remove("is-visible"))
  }
}
